// Source of truth for all 5 policy pages. Keep copy concise and India-compliant.
// Pages render via /pages/Policy.jsx using react-router param :slug.

export const SUPPORT_EMAIL = "techneel23@gmail.com";
export const SITE_NAME = "Gareebo Ka Filmmaker";
export const BRAND_LEGAL = "Gareebo Ka Filmmaker (Legally owned by Nilesh Rajendra Barnawal)";

export const SOCIAL_LINKS = {
  instagram: { handle: "@neelv007", url: "https://instagram.com/neelv007" },
  twitter:   { handle: "@neelv007", url: "https://twitter.com/neelv007" },
  youtube:   { handle: "@gareebokafilmmaker", url: "https://youtube.com/@gareebokafilmmaker" },
  email:     { handle: SUPPORT_EMAIL,           url: `mailto:${SUPPORT_EMAIL}` },
};

export const POLICIES = {
  disclaimer: {
    slug: "disclaimer",
    title: "Disclaimer",
    updated: "June 2026",
    sections: [
      {
        heading: "General information",
        body: `The information, tools, downloads and AI-generated colour grades provided on ${SITE_NAME} are for general creative use only. ${BRAND_LEGAL} makes no warranty about the suitability, completeness, or accuracy of any output for a specific commercial or broadcast use case.`,
      },
      {
        heading: "AI-generated content",
        body: `Look-up Tables (LUTs), prompts and recipes generated through the Studio AI feature are produced by third-party large language models. While we curate, validate and post-process every output, you are responsible for reviewing the result before using it in any final delivery, on-air broadcast or paid client work.`,
      },
      {
        heading: "External links",
        body: `Our pages may link to external tutorials, asset libraries or vendor sites. ${SITE_NAME} is not responsible for the content, accuracy or availability of any external resource.`,
      },
      {
        heading: "No professional advice",
        body: `Nothing on this site constitutes professional cinematography, legal, financial or business advice. Always consult a qualified professional for high-stakes decisions.`,
      },
      {
        heading: "Contact",
        body: `Questions about this disclaimer? Reach us at ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  privacy: {
    slug: "privacy",
    title: "Privacy Policy",
    updated: "June 2026",
    sections: [
      {
        heading: "Who we are",
        body: `${BRAND_LEGAL} ("we", "our", "us") operates the website and Studio at ${SITE_NAME}. We process personal data in accordance with India's Digital Personal Data Protection Act, 2023.`,
      },
      {
        heading: "Data we collect",
        body: `Account: email address and a bcrypt-hashed password. Usage: project metadata (uploaded filename, scene analysis result, render previews) — we do NOT store your raw video footage beyond what is needed to generate your LUT, and we permanently delete project assets after 30 days. Payments: PhonePe and / or Cashfree order IDs and amounts; we never see or store your card / UPI credentials. Analytics: anonymised page-view and click events via PostHog.`,
      },
      {
        heading: "How we use it",
        body: `To authenticate you, run the AI colour-grading pipeline, send transactional emails about subscription renewal or payment status, and improve the product. We do NOT sell, rent, or share your personal data with advertisers or third-party data brokers.`,
      },
      {
        heading: "Data retention",
        body: `Account email is retained while your account exists plus 90 days. Uploaded video/reference files are deleted 30 days after upload. Payment records are retained for 7 years to comply with Indian tax law.`,
      },
      {
        heading: "Your rights",
        body: `You can request access, correction, portability or deletion of your data at any time by emailing ${SUPPORT_EMAIL}. We respond within 30 days.`,
      },
      {
        heading: "Cookies",
        body: `We use a single first-party cookie/localStorage entry ("cinelut_token") to keep you logged in, plus anonymised PostHog analytics cookies. No advertising cookies are set.`,
      },
      {
        heading: "Contact",
        body: `Privacy concerns? Email ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  refund: {
    slug: "refund",
    title: "Refund Policy",
    updated: "June 2026",
    sections: [
      {
        heading: "No refunds",
        body: `All purchases on ${SITE_NAME} — including the ₹199/month Creator subscription and every credit top-up pack — are FINAL AND NON-REFUNDABLE. Because every payment unlocks immediate access to AI compute, downloadable digital assets and credit balances that cannot be revoked once consumed, we do not issue refunds for any reason.`,
      },
      {
        heading: "Failed transactions",
        body: `If your payment is debited from your bank but your plan or credits are not granted within 30 minutes, email ${SUPPORT_EMAIL} with the PhonePe / Cashfree order ID. We will reconcile and either grant the entitlement or, only in the rare case of a verified duplicate charge, raise a refund directly with PhonePe or Cashfree (whichever processed the payment) within 7 working days.`,
      },
      {
        heading: "Subscription cancellation",
        body: `You can cancel renewal at any time from your account — your current Creator cycle stays active until the end date, but you will not be billed again. No partial refunds are issued for unused days.`,
      },
      {
        heading: "Contact",
        body: `Billing questions: ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  terms: {
    slug: "terms",
    title: "Terms & Conditions",
    updated: "June 2026",
    sections: [
      {
        heading: "Acceptance of terms",
        body: `By creating an account or using ${SITE_NAME}, you agree to be bound by these Terms & Conditions. If you do not agree, do not use the service.`,
      },
      {
        heading: "Eligibility",
        body: `You must be at least 13 years old to create an account, and at least 18 to subscribe to the paid Creator plan. By subscribing you confirm the payment method belongs to you or that you are authorised to use it.`,
      },
      {
        heading: "Acceptable use",
        body: `You will not (a) upload content you do not own the rights to, (b) attempt to reverse-engineer the LUT engine, (c) resell or sublicense generated LUTs as branded ${SITE_NAME} output, (d) abuse the AI prompt endpoint to generate disallowed content, or (e) circumvent the credit / plan gates.`,
      },
      {
        heading: "Subscription & auto-downgrade",
        body: `The Creator plan costs ₹199/month and grants 60 AI credits plus unlimited rule-based LUTs. If you do not renew within 24 hours of expiry, your plan is automatically downgraded to Free and ALL credits (subscription + top-ups) are wiped. A 7-day win-back window lets you reactivate and restore the wiped balance.`,
      },
      {
        heading: "Intellectual property",
        body: `You retain full ownership of your uploaded source footage, reference images and the LUTs we generate from them. We retain ownership of the engine, model fine-tunes, presets, and brand assets. By uploading you grant us a limited licence solely to process your files for the requested operation.`,
      },
      {
        heading: "Limitation of liability",
        body: `To the maximum extent permitted by Indian law, our total liability arising from your use of ${SITE_NAME} shall not exceed the total amount paid by you to us in the preceding 30 days.`,
      },
      {
        heading: "Governing law",
        body: `These terms are governed by the laws of India. Any dispute shall be subject to the exclusive jurisdiction of courts in Mumbai, Maharashtra.`,
      },
      {
        heading: "Changes to terms",
        body: `We may update these terms occasionally. Material changes will be announced in-app at least 7 days before they take effect.`,
      },
      {
        heading: "Contact",
        body: `Legal queries: ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  shipping: {
    slug: "shipping",
    title: "Shipping Policy",
    updated: "June 2026",
    sections: [
      {
        heading: "Fully digital product",
        body: `${SITE_NAME} is a 100% digital service. We do NOT ship any physical goods offline — no CDs, USBs, prints or packaging are mailed at any point.`,
      },
      {
        heading: "Digital delivery",
        body: `All purchases (Creator subscription + credit packs) are delivered instantly to your account once the payment is confirmed by PhonePe or Cashfree (whichever gateway is active at checkout). You can download your generated .cube / .3dl / .look files, presets and music directly from the Studio or Premium Add-ons pages.`,
      },
      {
        heading: "Delivery failures",
        body: `If your subscription or credits do not show up in your account within 30 minutes of a successful payment, email ${SUPPORT_EMAIL} with the PhonePe / Cashfree order ID and we will reconcile within 1 working day.`,
      },
      {
        heading: "Contact",
        body: `Delivery issues: ${SUPPORT_EMAIL}.`,
      },
    ],
  },

  about: {
    slug: "about",
    title: "About",
    updated: "June 2026",
    sections: [
      {
        heading: "Who runs this",
        body: `${SITE_NAME} is the YouTube channel + film-tech lab where I share colour science, indie cinematography breakdowns and the tools I build for filmmakers who can't afford a Hollywood-grade pipeline. The website and the CineLUT AI Studio you're using right now are operated and legally owned by Nilesh Rajendra Barnawal (creator handle: NeelVarma).`,
      },
      {
        heading: "Why I built CineLUT AI",
        body: `Every week I get DMs from filmmakers asking, "Bhai, kaunsa LUT use kiya?" The honest answer is that there is no single LUT — every shot is a hand-graded conversation between footage, reference, and intent. CineLUT AI is my attempt to bottle that conversation into a one-click, AI-assisted tool so indie filmmakers, students and YouTubers can grade like a feature-film colourist without the ₹15-lakh seat.`,
      },
      {
        heading: "Built with",
        body: `LAB-space colour transfer + a 33³ 3D LUT engine in OpenCV / NumPy, Gemini Vision for scene-aware AI prompts, FastAPI + MongoDB on the server, React + Tailwind on the client. Payments are handled by PhonePe and Cashfree — we never see or store your card / UPI credentials.`,
      },
      {
        heading: "Legal entity",
        body: `${BRAND_LEGAL}. Based in India. All ₹ amounts are inclusive of taxes shown at checkout (Creator subscription includes 18% GST).`,
      },
      {
        heading: "Stay in touch",
        body: `YouTube · ${SOCIAL_LINKS.youtube.handle}. Instagram · ${SOCIAL_LINKS.instagram.handle}. Twitter / X · ${SOCIAL_LINKS.twitter.handle}. Email · ${SUPPORT_EMAIL}. I read every message myself.`,
      },
    ],
  },
};

export const POLICY_ORDER = ["disclaimer", "privacy", "refund", "terms", "shipping", "about"];
